import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CHAT_ANIMATIONS } from '@/utils/chat-animations';
import { Plus, Check, AlertCircle, Loader2, CheckSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import GlassPill from '@/components/ui/GlassPill';
import { ExtractedTask } from '@/hooks/ai-agents/functions/core/useRecommendationDetection';
import { useThreadContext } from '@/hooks/ai-agents/functions/core/useThreadContext';
import { useAgentChatTaskCreationWithTracking } from '@/hooks/ai-agents/functions/core/useAgentChatTaskCreationWithTracking';
import { useRecommendationCardState, RECOMMENDATION_CONFIGS } from '@/hooks/ai-agents/functions/core/createRecommendationTrackingWrapper';
import { useRecommendationTracking } from '@/hooks/ai-agents/functions/core/useRecommendationTracking';
import { useViewRecommendationNavigation } from '@/hooks/ai-agents/functions/core/useViewRecommendationNavigation';
import { useUser } from '@/hooks/auth/use-user';
import { useNavigate } from 'react-router-dom';
import { 
  getProjectInfoByGoalId,
  getProjectNavigationUrl,
  getGoalNavigationUrl,
  useNavigationWithModalClose
} from '@/hooks/ai-agents/functions/core/useResponseParsing';
import { cn } from '@/lib/utils';
import { MessageDisplay } from '@/hooks/ai-agents/types/chat-hook-types';
import { 
  getCardContainerClasses, 
  getCardIconClasses, 
  getCardButtonClasses 
} from './shared/RecommendationCardStyles';

type SaveState = 'idle' | 'saving' | 'success' | 'saved' | 'error';

interface ChatRecommendedTaskCardProps {
  task: ExtractedTask; // Direct task data instead of parsing from message
  message: MessageDisplay; // Original message for tracking purposes
  type?: 'task';
  className?: string;
}

/**
 * Chat Recommended Task Card - Individual task item with save button
 * Shows task recommendations with option to save to goal task board
 * Now with standardized recommendation tracking and direct data passing
 */
export const ChatRecommendedTaskCard: React.FC<ChatRecommendedTaskCardProps> = ({
  task,
  message,
  type = 'task',
  className
}) => {
  const { user } = useUser();
  const { navigateAndCloseModal } = useNavigationWithModalClose();
  
  // Get thread context to find the goal ID (fallback if task doesn't have goalId)
  const { data: threadContext } = useThreadContext(message.threadId);
  
  // Use standardized tracking state management
  const { localState, setLocalState } = useRecommendationCardState(message, 'task');
  
  // Task creation with tracking
  const { mutateWithTracking, isGenerating } = useAgentChatTaskCreationWithTracking(message);
  
  // Get saved recommendation data for view functionality
  const { isRecommendationSaved } = useRecommendationTracking(message.id, message.databaseId);
  
  // View navigation functionality
  const { viewTask } = useViewRecommendationNavigation();
  
  // Handle viewing the saved task
  const handleViewTask = async () => {
    const status = await isRecommendationSaved('task');
    if (status.isSaved && status.savedItemId && status.metadata) {
      const savedRecommendation = {
        id: status.savedItemId,
        type: 'task' as const,
        savedAt: status.savedAt!,
        metadata: status.metadata
      };
      viewTask(savedRecommendation);
    } else {
      console.error('[ChatRecommendedTaskCard] No saved task data found');
    }
  };
  
  // Get config for this recommendation type
  const config = RECOMMENDATION_CONFIGS.task;
  
  // Tasks now always render and go to personal task manager (no project dependencies)

  // Handle saving this specific task
  const handleSaveTask = async () => {
    console.log('[ChatRecommendedTaskCard] 🚀 STARTING handleSaveTask');
    console.log('[ChatRecommendedTaskCard] 🔍 User info:', { userId: user?.id, hasUser: !!user });
    
    if (!user?.id) {
      console.log('[ChatRecommendedTaskCard] ❌ No user ID, returning early');
      return;
    }
    
    console.log('[ChatRecommendedTaskCard] 🔄 Setting state to saving...');
    setLocalState('saving');
    
    // Personal task data (no project context)
    const finalTaskData = {
      title: task.title,
      description: task.description || '',
      dueDate: null,
      priority: null
      // No goalId - personal task
    };
    
    console.log('[ChatRecommendedTaskCard] �� Task parameters:', finalTaskData);
    console.log('[ChatRecommendedTaskCard] 🔍 mutateWithTracking function:', typeof mutateWithTracking);
    
    try {
      console.log('[ChatRecommendedTaskCard] 🔄 Calling mutateWithTracking...');
      const result = await mutateWithTracking(finalTaskData);
      console.log('[ChatRecommendedTaskCard] ✅ mutateWithTracking completed:', result);
      
      console.log('[ChatRecommendedTaskCard] 🔄 Setting state to saved...');
      setLocalState('saved');
      
    } catch (error) {
      console.error('[ChatRecommendedTaskCard] ❌ Failed to save task:', error);
      setLocalState('error');
      
      // Reset to idle after 3 seconds
      setTimeout(() => {
        setLocalState('idle');
      }, 3000);
    }
  };

  // Get button content based on state
  const getButtonContent = () => {
    switch (localState) {
      case 'saving':
        return <Loader2 size={12} className="animate-spin" />;
      case 'success':
        return <Check size={12} />;
      case 'saved':
        return <CheckSquare size={12} />;
      case 'error':
        return <AlertCircle size={12} />;
      default:
        return <Plus size={12} />;
    }
  };

  // Get button text based on state
  const getButtonText = () => {
    switch (localState) {
      case 'saving':
        return config.savingText;
      case 'success':
        return config.successText;
      case 'saved':
        return config.savedText;
      case 'error':
        return config.errorText;
      default:
        return config.saveText;
    }
  };

  // Get button click handler based on state
  const getButtonHandler = () => {
    switch (localState) {
      case 'saved':
        return handleViewTask;
      default:
        return handleSaveTask;
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        variants={CHAT_ANIMATIONS.cardEntry}
        initial="initial"
        animate="animate"
        exit="exit"
        className={cn("mt-3 w-full", className)}
      >
        <div className={cn(getCardContainerClasses(type))}>
          <div className="flex items-start gap-2.5">
            {/* Save Button - Left Side */}
            <div className="flex-shrink-0 pt-0.5">
              <Button
                size="sm"
                variant="outline"
                onClick={getButtonHandler()}
                disabled={localState === 'saving' || localState === 'success'}
                className={cn(
                  "h-7 px-3 text-xs border-amber-300 dark:border-amber-600",
                  "hover:bg-amber-50 dark:hover:bg-amber-900/20",
                  "transition-all duration-200",
                  // State-specific styling
                  localState === 'success' && 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400',
                  localState === 'saved' && 'border-gray-300 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200',
                  localState === 'error' && 'border-red-500 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400',
                )}
              >
                <span className="flex items-center gap-1.5">
                  {getButtonContent()}
                  <span>{getButtonText()}</span>
                </span>
              </Button>
            </div>
            
            {/* Content */}
            <div className="flex-1 min-w-0">
              <span className="text-sm text-gray-700 dark:text-gray-200 block font-medium">
                {task.title}
              </span>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                {task.description}
              </p>
              
              {/* Personal Task - No project navigation needed */}
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}; 